import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";

// --- Types ---

export type GoogleReviewsOauthConfigResponse = {
  authorization_url: string;
  state: string;
};

export type GoogleReviewsOauthAuthorizeRequest = {
  code: string;
  state: string;
  clinic_id: number;
};

export type GoogleReviewConnection = {
  id: number;
  clinic_id: number;
  clinic_name: string;
  google_account_id: string;
  google_location_id: string;
  location_name: string;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_error: string | null;
  consecutive_sync_failures: number;
  created_at: string;
};

export type GoogleReviewPatient = {
  id: number;
  first_name: string;
  last_name: string;
  appointment_date?: string | null;
  provider_name?: string | null;
};

export type GoogleReview = {
  id: number;
  clinic_id: number;
  clinic_name: string;
  google_review_id: string;
  reviewer_display_name: string;
  reviewer_profile_photo_url: string | null;
  star_rating: number;
  comment: string | null;
  google_created_at: string;
  google_updated_at: string;
  reply_text: string | null;
  reply_at: string | null;
  reply_synced_to_google: boolean;
  reply_sync_error: string | null;
  patient: GoogleReviewPatient | null;
  match_confidence: number | null;
  match_method: string | null;
  created_at: string;
};

export type GoogleReviewsListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: GoogleReview[];
};

export type GoogleReviewsFilter = {
  clinic_id?: number | number[];
  star_rating?: number | number[];
  has_reply?: boolean;
  has_patient?: boolean;
  date_from?: string;
  date_to?: string;
  patient_id?: number;
  page?: number;
  page_size?: number;
};

export type GoogleReviewSummary = {
  total_reviews: number;
  avg_rating: number;
  unreplied_count: number;
  matched_count: number;
  match_rate: number;
  rating_distribution: Record<string, number>;
  reviews_this_month: number;
  reviews_last_month: number;
};

export type GoogleReviewReplyResponse = {
  reply_text: string;
  reply_at: string;
  reply_synced_to_google: boolean;
};

export type GoogleReviewMatchResponse = {
  patient_id: number;
  match_method: string;
};

// --- API ---

export const googleReviewsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // OAuth
    getGoogleReviewsOauthConfig: builder.query<
      GoogleReviewsOauthConfigResponse,
      void
    >({
      query: () => "/google-reviews/oauth/config/"
    }),

    authorizeGoogleReviewsOauth: builder.mutation<
      { connections: GoogleReviewConnection[] },
      GoogleReviewsOauthAuthorizeRequest
    >({
      query: (body) => ({
        url: "/google-reviews/oauth/authorize/",
        method: "POST",
        body
      }),
      invalidatesTags: ["GoogleReviewConnections"]
    }),

    // Connections
    getGoogleReviewConnections: builder.query<GoogleReviewConnection[], void>({
      query: () => "/google-reviews/connections/",
      providesTags: ["GoogleReviewConnections"]
    }),

    deleteGoogleReviewConnection: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/connections/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviewConnections"]
    }),

    // Reviews
    getGoogleReviews: builder.query<
      GoogleReviewsListResponse,
      GoogleReviewsFilter
    >({
      query: (filters) => {
        const params = formatValidQueryParamUrlCommaSeparated(filters);
        return `/google-reviews/reviews/?${params}`;
      },
      providesTags: ["GoogleReviews"]
    }),

    getGoogleReview: builder.query<GoogleReview, number>({
      query: (id) => `/google-reviews/reviews/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "GoogleReviews", id }]
    }),

    // Summary
    getGoogleReviewSummary: builder.query<
      GoogleReviewSummary,
      { clinic_id?: number } | void
    >({
      query: (params) => {
        if (params && params.clinic_id) {
          return `/google-reviews/summary/?clinic_id=${params.clinic_id}`;
        }
        return "/google-reviews/summary/";
      },
      providesTags: ["GoogleReviewSummary"]
    }),

    // Reply
    replyToGoogleReview: builder.mutation<
      GoogleReviewReplyResponse,
      { id: number; text: string }
    >({
      query: ({ id, text }) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "POST",
        body: { text }
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    editGoogleReviewReply: builder.mutation<
      GoogleReviewReplyResponse,
      { id: number; text: string }
    >({
      query: ({ id, text }) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "PUT",
        body: { text }
      }),
      invalidatesTags: ["GoogleReviews"]
    }),

    deleteGoogleReviewReply: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/reviews/${id}/reply/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    // Patient Match
    matchGoogleReviewPatient: builder.mutation<
      GoogleReviewMatchResponse,
      { id: number; patient_id: number }
    >({
      query: ({ id, patient_id }) => ({
        url: `/google-reviews/reviews/${id}/match/`,
        method: "POST",
        body: { patient_id }
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    }),

    unmatchGoogleReviewPatient: builder.mutation<void, number>({
      query: (id) => ({
        url: `/google-reviews/reviews/${id}/match/`,
        method: "DELETE"
      }),
      invalidatesTags: ["GoogleReviews", "GoogleReviewSummary"]
    })
  })
});

export const {
  useGetGoogleReviewsOauthConfigQuery,
  useLazyGetGoogleReviewsOauthConfigQuery,
  useAuthorizeGoogleReviewsOauthMutation,
  useGetGoogleReviewConnectionsQuery,
  useDeleteGoogleReviewConnectionMutation,
  useGetGoogleReviewsQuery,
  useGetGoogleReviewQuery,
  useGetGoogleReviewSummaryQuery,
  useReplyToGoogleReviewMutation,
  useEditGoogleReviewReplyMutation,
  useDeleteGoogleReviewReplyMutation,
  useMatchGoogleReviewPatientMutation,
  useUnmatchGoogleReviewPatientMutation
} = googleReviewsApi;
